import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Card, CardContent, Divider, Tab, Tabs } from '@mui/material';

const commonTabSx = {
  minHeight: 48,
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 500,
  color: '#424242',

  '& .MuiSvgIcon-root': {
    fontSize: 18,
    mr: 0.75
  },

  '&.Mui-selected': {
    color: '#5697D4'
  }
};

const cardSx = {
  border: '1px solid #E0E0E0',
  borderRadius: '10px',
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden'
};

const tabsSx = {
  minHeight: 48,

  '& .MuiTabs-indicator': {
    height: 2,
    backgroundColor: '#3F83C5'
  }
};

const cardContentSx = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
  p: 0,

  '&:last-child': {
    pb: 0
  }
};

const CommonTabCard = ({
  tabs,
  defaultValue,
  onTabChange,
  cardStyles,
  tabStyles,
  tabsStyles
}) => {
  const [value, setValue] = useState(defaultValue);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    onTabChange?.(newValue);
  };

  const activeTab = tabs.find(tab => tab.value === value);

  return (
    <Card
      sx={{
        ...cardSx,
        ...cardStyles
      }}>
      <Tabs
        value={value}
        onChange={handleChange}
        variant="fullWidth"
        aria-label="content tabs"
        sx={{
          ...tabsSx,
          flexShrink: 0,
          ...tabsStyles
        }}>
        {tabs.map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            icon={tab.icon}
            iconPosition={tab.iconPosition || 'start'}
            label={tab.label}
            disabled={tab.disabled}
            sx={{
              ...commonTabSx,
              ...tabStyles,
              ...tab.sx
            }}
          />
        ))}
      </Tabs>

      <Divider sx={{ flexShrink: 0 }} />

      <CardContent sx={cardContentSx}>
        <Box
          role="tabpanel"
          id={`common-tabpanel-${value}`}
          aria-labelledby={`common-tab-${value}`}
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          }}>
          {activeTab?.content}
        </Box>
      </CardContent>
    </Card>
  );
};

CommonTabCard.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      iconPosition: PropTypes.oneOf(['top', 'bottom', 'start', 'end']),
      content: PropTypes.node,
      disabled: PropTypes.bool,
      sx: PropTypes.object
    })
  ).isRequired,
  defaultValue: PropTypes.number,
  onTabChange: PropTypes.func,
  cardStyles: PropTypes.object,
  tabStyles: PropTypes.object,
  tabsStyles: PropTypes.object
};

CommonTabCard.defaultProps = {
  defaultValue: 0,
  onTabChange: undefined,
  cardStyles: {},
  tabStyles: {},
  tabsStyles: {}
};

export default CommonTabCard;
